import chartAssets from '../../chart-assets.json';
import type { ChartModel, PlotKind } from './types';

export interface HighchartsRuntime {
  Chart: new (options: Record<string, unknown>) => unknown;
  setOptions(options: Record<string, unknown>): void;
}

declare global {
  interface Window {
    Highcharts?: HighchartsRuntime;
    HighchartsAdapter?: unknown;
  }
}

const loaded = new Map<string, Promise<void>>();
const HIGHCHARTS_MORE_SERIES_TYPES = new Set([
  'arearange',
  'areasplinerange',
  'boxplot',
  'bubble',
  'columnrange',
  'errorbar',
  'gauge',
  'polygon',
  'waterfall'
]);

export function chartRuntimeRequiresHighchartsMore(
  kind: PlotKind,
  model?: Pick<ChartModel, 'chartType' | 'polar' | 'series'>
): boolean {
  if (kind === 'average-series' || kind === 'day-polar' || model?.polar === true) return true;
  if (model?.chartType && HIGHCHARTS_MORE_SERIES_TYPES.has(model.chartType)) return true;
  return model?.series.some((series) => Boolean(series.type && HIGHCHARTS_MORE_SERIES_TYPES.has(series.type))) ?? false;
}

export function chartRuntimeAssetsForKinds(kinds: readonly PlotKind[]): string[] {
  if (kinds.length === 0) return [];
  const sources = [...chartAssets.core];
  if (kinds.some((kind) => chartRuntimeRequiresHighchartsMore(kind))) {
    sources.push(...chartAssets.average);
  }
  if (kinds.includes('heatmap')) sources.push(...chartAssets.heatmap);
  sources.push(...chartAssets.common);
  return [...new Set(sources)];
}

/** Starts workspace-specific downloads while leaving dependency-ordered execution to ensureHighcharts. */
export function preloadChartRuntime(kinds: readonly PlotKind[], ownerDocument: Document = document): HTMLLinkElement[] {
  const links: HTMLLinkElement[] = [];
  for (const source of chartRuntimeAssetsForKinds(kinds)) {
    const existing = Array.from(ownerDocument.querySelectorAll<HTMLLinkElement>('link[data-pq-chart-preload]'))
      .find((link) => link.dataset.pqChartPreload === source);
    if (existing) {
      links.push(existing);
      continue;
    }
    const link = ownerDocument.createElement('link');
    link.rel = 'preload';
    link.as = 'script';
    link.href = source;
    link.dataset.pqChartPreload = source;
    ownerDocument.head.append(link);
    links.push(link);
  }
  return links;
}

function loadScript(src: string): Promise<void> {
  const existing = loaded.get(src);
  if (existing) return existing;
  const promise = new Promise<void>((resolve, reject) => {
    const present = document.querySelector<HTMLScriptElement>(`script[data-pq-source="${src}"]`);
    if (present?.dataset.loaded === 'true') {
      resolve();
      return;
    }
    const script = present ?? document.createElement('script');
    script.src = src;
    script.async = false;
    script.dataset.pqSource = src;
    script.addEventListener('load', () => {
      script.dataset.loaded = 'true';
      resolve();
    }, { once: true });
    script.addEventListener('error', () => reject(new Error(`Unable to load bundled chart module ${src}`)), { once: true });
    if (!present) document.head.append(script);
  });
  loaded.set(src, promise);
  void promise.catch(() => loaded.delete(src));
  return promise;
}

export async function ensureHighcharts(
  kind: PlotKind,
  model?: Pick<ChartModel, 'chartType' | 'polar' | 'series'>
): Promise<HighchartsRuntime> {
  if (!window.Highcharts) {
    for (const source of chartAssets.core) await loadScript(source);
  }
  if (chartRuntimeRequiresHighchartsMore(kind, model)) {
    for (const source of chartAssets.average) await loadScript(source);
  }
  if (kind === 'heatmap') {
    for (const source of chartAssets.heatmap) await loadScript(source);
  }
  for (const source of chartAssets.common) await loadScript(source);
  if (!window.Highcharts) throw new Error('Bundled Highcharts did not initialize');
  return window.Highcharts;
}
